import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { SERVICE_OPTIONS } from "../data/services";

const schema = z.object({
    name: z.string().trim().min(2, "Please enter your name.").max(100),
    work_email: z.string().trim().email("Please enter a valid work email.").max(150),
    company: z.string().trim().max(150).optional().or(z.literal("")),
    service_of_interest: z.string().min(1, "Please choose a service."),
    message: z.string().trim().min(10, "Tell us a little more (at least 10 characters).").max(2000),
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = ({ defaultService = "" }) => {
    const [result, setResult] = useState(null);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { name: "", work_email: "", company: "", service_of_interest: defaultService, message: "" },
    });

    const onSubmit = async (data) => {
        try {
            const res = await axios.post(`${API}/enquiries`, data);
            setResult(res.data);
            reset();
            toast.success("Enquiry sent — thank you for reaching out.");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
        }
    };

    if (result) {
        return (
            <div className="glass rounded-3xl p-10 text-center" data-testid="contact-success-panel" role="status">
                <CheckCircle2 size={44} className="mx-auto text-cyan-300" aria-hidden="true" />
                <h3 className="mt-5 text-2xl font-semibold">Thank you — we've received your enquiry.</h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
                    {result.message} A member of our team will review it and respond as soon as possible.
                </p>
                <p className="mt-4 text-xs text-slate-500" style={{ fontFamily: "Fira Code, monospace" }}>
                    Reference: {result.id}
                </p>
                <button type="button" className="btn-ghost mt-7" onClick={() => setResult(null)} data-testid="contact-send-another-button">
                    Send another message
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="glass rounded-3xl p-6 sm:p-10" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <label htmlFor="cf-name" className="mb-2 block text-sm font-medium text-slate-300">
                        Name <span className="text-cyan-300" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="cf-name"
                        type="text"
                        autoComplete="name"
                        placeholder="Your full name"
                        className={`field ${errors.name ? "field-error" : ""}`}
                        aria-invalid={!!errors.name}
                        data-testid="contact-name-input"
                        {...register("name")}
                    />
                    {errors.name && <p className="mt-2 text-xs text-rose-400" role="alert" data-testid="contact-name-error">{errors.name.message}</p>}
                </div>

                <div>
                    <label htmlFor="cf-email" className="mb-2 block text-sm font-medium text-slate-300">
                        Work email <span className="text-cyan-300" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="cf-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        className={`field ${errors.work_email ? "field-error" : ""}`}
                        aria-invalid={!!errors.work_email}
                        data-testid="contact-email-input"
                        {...register("work_email")}
                    />
                    {errors.work_email && <p className="mt-2 text-xs text-rose-400" role="alert" data-testid="contact-email-error">{errors.work_email.message}</p>}
                </div>

                <div>
                    <label htmlFor="cf-company" className="mb-2 block text-sm font-medium text-slate-300">Company</label>
                    <input
                        id="cf-company"
                        type="text"
                        autoComplete="organization"
                        placeholder="Your organization"
                        className="field"
                        data-testid="contact-company-input"
                        {...register("company")}
                    />
                </div>

                <div>
                    <label htmlFor="cf-service" className="mb-2 block text-sm font-medium text-slate-300">
                        Service of interest <span className="text-cyan-300" aria-hidden="true">*</span>
                    </label>
                    <select
                        id="cf-service"
                        className={`field ${errors.service_of_interest ? "field-error" : ""}`}
                        aria-invalid={!!errors.service_of_interest}
                        data-testid="contact-service-select"
                        {...register("service_of_interest")}
                    >
                        <option value="" disabled>Select a service</option>
                        {SERVICE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    {errors.service_of_interest && <p className="mt-2 text-xs text-rose-400" role="alert" data-testid="contact-service-error">{errors.service_of_interest.message}</p>}
                </div>

                <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="mb-2 block text-sm font-medium text-slate-300">
                        Message <span className="text-cyan-300" aria-hidden="true">*</span>
                    </label>
                    <textarea
                        id="cf-message"
                        rows={5}
                        placeholder="Tell us about your business need or project…"
                        className={`field resize-y ${errors.message ? "field-error" : ""}`}
                        aria-invalid={!!errors.message}
                        data-testid="contact-message-input"
                        {...register("message")}
                    />
                    {errors.message && <p className="mt-2 text-xs text-rose-400" role="alert" data-testid="contact-message-error">{errors.message.message}</p>}
                </div>
            </div>

            <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs leading-relaxed text-slate-500">
                    We only use your details to respond to this enquiry.
                </p>
                <button type="submit" className="btn-primary" disabled={isSubmitting} data-testid="contact-submit-button">
                    {isSubmitting ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
                    {isSubmitting ? "Sending…" : "Send Enquiry"}
                </button>
            </div>
        </form>
    );
};
